import { Component } from '@theme/component';

/**
 * クリニック一覧の表示順・絞り込み・ページャー。
 *
 * Shopifyのテーマエディタはブロックをセクションまたぎで並べ替えられないため、
 * 表示順をDOMの物理順に依存させると運用開始後に並び順が崩れる。
 * そこでページ内の全カードをこのコンポーネントのグリッドへ集約し、
 * 「都道府県（JISコード順）→ クリニック名」でソートしてから描画する。
 * これにより、どのセクションにブロックを追加しても正しい位置に表示される。
 */

/**
 * 都道府県。配列の順序がJISコード順（北から南）で、そのまま表示順の基準になる。
 * slug はURLクエリ（?pref=tokyo）に使う。
 *
 * @type {{ name: string, slug: string }[]}
 */
const PREFECTURES = [
  { name: '北海道', slug: 'hokkaido' },
  { name: '青森県', slug: 'aomori' },
  { name: '岩手県', slug: 'iwate' },
  { name: '宮城県', slug: 'miyagi' },
  { name: '秋田県', slug: 'akita' },
  { name: '山形県', slug: 'yamagata' },
  { name: '福島県', slug: 'fukushima' },
  { name: '茨城県', slug: 'ibaraki' },
  { name: '栃木県', slug: 'tochigi' },
  { name: '群馬県', slug: 'gunma' },
  { name: '埼玉県', slug: 'saitama' },
  { name: '千葉県', slug: 'chiba' },
  { name: '東京都', slug: 'tokyo' },
  { name: '神奈川県', slug: 'kanagawa' },
  { name: '新潟県', slug: 'niigata' },
  { name: '富山県', slug: 'toyama' },
  { name: '石川県', slug: 'ishikawa' },
  { name: '福井県', slug: 'fukui' },
  { name: '山梨県', slug: 'yamanashi' },
  { name: '長野県', slug: 'nagano' },
  { name: '岐阜県', slug: 'gifu' },
  { name: '静岡県', slug: 'shizuoka' },
  { name: '愛知県', slug: 'aichi' },
  { name: '三重県', slug: 'mie' },
  { name: '滋賀県', slug: 'shiga' },
  { name: '京都府', slug: 'kyoto' },
  { name: '大阪府', slug: 'osaka' },
  { name: '兵庫県', slug: 'hyogo' },
  { name: '奈良県', slug: 'nara' },
  { name: '和歌山県', slug: 'wakayama' },
  { name: '鳥取県', slug: 'tottori' },
  { name: '島根県', slug: 'shimane' },
  { name: '岡山県', slug: 'okayama' },
  { name: '広島県', slug: 'hiroshima' },
  { name: '山口県', slug: 'yamaguchi' },
  { name: '徳島県', slug: 'tokushima' },
  { name: '香川県', slug: 'kagawa' },
  { name: '愛媛県', slug: 'ehime' },
  { name: '高知県', slug: 'kochi' },
  { name: '福岡県', slug: 'fukuoka' },
  { name: '佐賀県', slug: 'saga' },
  { name: '長崎県', slug: 'nagasaki' },
  { name: '熊本県', slug: 'kumamoto' },
  { name: '大分県', slug: 'oita' },
  { name: '宮崎県', slug: 'miyazaki' },
  { name: '鹿児島県', slug: 'kagoshima' },
  { name: '沖縄県', slug: 'okinawa' },
];

const PREF_ORDER = new Map(PREFECTURES.map((pref, index) => [pref.name, index]));
const PREF_SLUG = new Map(PREFECTURES.map((pref) => [pref.name, pref.slug]));
const PREF_NAME = new Map(PREFECTURES.map((pref) => [pref.slug, pref.name]));

/** 未知の都道府県名は末尾に寄せる（入力ゆれで並び順が壊れないようにする） */
const prefOrder = (/** @type {string} */ name) => PREF_ORDER.get(name) ?? PREFECTURES.length;

const collator = new Intl.Collator('ja');

const SEARCH_DEBOUNCE_MS = 150;

/**
 * @typedef {Object} Card
 * @property {HTMLElement} el
 * @property {string} pref
 * @property {string} name
 * @property {string} search
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} count
 * @property {HTMLElement} [chips]
 * @property {HTMLInputElement} [search]
 * @property {HTMLElement} [pager]
 * @property {HTMLElement} [noResults]
 *
 * @extends {Component<Refs>}
 */
class ClinicList extends Component {
  requiredRefs = ['grid', 'count'];

  /** @type {Card[]} */
  #cards = [];

  /** @type {Card[]} */
  #filtered = [];

  #state = { pref: '', query: '', page: 1 };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #searchTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#collect();
    this.#restoreFromUrl();
    this.#buildChips();

    this.refs.chips?.addEventListener('click', this.#handleChipClick);
    this.refs.search?.addEventListener('input', this.#handleSearchInput);
    this.refs.search?.addEventListener('compositionend', this.#handleSearchCommit);
    this.refs.pager?.addEventListener('click', this.#handlePagerClick);
    window.addEventListener('popstate', this.#handlePopState);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', this.#handleSectionChange);
      document.addEventListener('shopify:section:unload', this.#handleSectionChange);
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    this.#render({ updateUrl: false });
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#searchTimer);
    this.refs.chips?.removeEventListener('click', this.#handleChipClick);
    this.refs.search?.removeEventListener('input', this.#handleSearchInput);
    this.refs.search?.removeEventListener('compositionend', this.#handleSearchCommit);
    this.refs.pager?.removeEventListener('click', this.#handlePagerClick);
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('shopify:section:load', this.#handleSectionChange);
    document.removeEventListener('shopify:section:unload', this.#handleSectionChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);

    // テーマエディタがこのセクションを再描画するとグリッドごと破棄されるため、
    // カードを元のセクションへ返してから離脱する。返さないとカードが失われる。
    this.#releaseCards();
  }

  get #perPage() {
    const value = Number.parseInt(this.dataset.perPage ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 50;
  }

  /* ------------------------------------------------------------------ 収集 */

  /**
   * ページ内の全 clinic-list セクションからカードをグリッドへ移動し、
   * 都道府県順・名称順に並べ替える。
   */
  #collect() {
    const { grid } = this.refs;
    const incoming = document.createDocumentFragment();

    for (const source of document.querySelectorAll('[data-clinic-source]')) {
      incoming.append(...source.querySelectorAll('[data-clinic-card]'));
    }
    grid.append(incoming);

    this.#cards = Array.from(grid.querySelectorAll('[data-clinic-card]'), (el) => ({
      el: /** @type {HTMLElement} */ (el),
      pref: (/** @type {HTMLElement} */ (el).dataset.pref ?? '').trim(),
      name: /** @type {HTMLElement} */ (el).dataset.name ?? '',
      search: /** @type {HTMLElement} */ (el).dataset.search ?? '',
    }));

    this.#cards.sort(
      (a, b) => prefOrder(a.pref) - prefOrder(b.pref) || collator.compare(a.name, b.name)
    );

    const ordered = document.createDocumentFragment();
    for (const card of this.#cards) ordered.append(card.el);
    grid.append(ordered);
  }

  /** カードを元のセクションへ戻す（セクションが消えている場合は破棄する） */
  #releaseCards() {
    for (const { el } of this.#cards) {
      el.classList.remove('clinic-card--hidden');

      const sectionId = el.dataset.sectionId ?? '';
      const source = sectionId
        ? document.querySelector(`[data-clinic-source][data-section-id="${CSS.escape(sectionId)}"]`)
        : null;

      if (source) source.append(el);
      else el.remove();
    }

    this.#cards = [];
    this.#filtered = [];
  }

  /* ------------------------------------------------------------ 絞り込みUI */

  #buildChips() {
    const { chips } = this.refs;
    if (!chips) return;

    // そのエリアに実際に存在する都道府県のみ（0件の県は出さない）
    const present = [...new Set(this.#cards.map((card) => card.pref).filter(Boolean))].sort(
      (a, b) => prefOrder(a) - prefOrder(b)
    );

    chips.replaceChildren();

    if (present.length < 2) {
      chips.hidden = true;
      return;
    }

    chips.hidden = false;
    chips.append(this.#createChip('', 'すべて'));
    for (const pref of present) chips.append(this.#createChip(pref, pref));
  }

  /**
   * @param {string} value 空文字は「すべて」
   * @param {string} label
   */
  #createChip(value, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'clinic-list__chip';
    button.textContent = label;
    button.dataset.prefValue = value;
    button.setAttribute('aria-pressed', 'false');
    return button;
  }

  /* -------------------------------------------------------------- イベント */

  #handleChipClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const chip = target.closest('[data-pref-value]');
    if (!(chip instanceof HTMLElement)) return;

    this.#state.pref = chip.dataset.prefValue ?? '';
    this.#state.page = 1;
    this.#render();
  };

  #handleSearchInput = () => {
    clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(this.#handleSearchCommit, SEARCH_DEBOUNCE_MS);
  };

  // IME確定（compositionend）でも即座に反映させる
  #handleSearchCommit = () => {
    clearTimeout(this.#searchTimer);

    const value = this.refs.search?.value ?? '';
    if (value === this.#state.query) return;

    this.#state.query = value;
    this.#state.page = 1;
    this.#render();
  };

  #handlePagerClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const link = target.closest('[data-page]');
    if (!(link instanceof HTMLElement)) return;
    // 新しいタブで開く操作はブラウザに任せる
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();

    this.#state.page = Number.parseInt(link.dataset.page ?? '1', 10) || 1;
    this.#render();
    this.refs.grid.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  #handlePopState = () => {
    this.#restoreFromUrl();
    this.#render({ updateUrl: false });
  };

  /** テーマエディタでセクションが追加・更新・削除されたら再収集する */
  #handleSectionChange = (/** @type {Event} */ event) => {
    const sectionId = /** @type {CustomEvent} */ (event).detail?.sectionId;

    // 再描画されたセクションの古いカードを取り除いてから収集し直す（重複防止）
    if (sectionId) {
      for (const { el } of this.#cards) {
        if (el.dataset.sectionId === sectionId) el.remove();
      }
    }

    this.#collect();
    this.#buildChips();
    this.#render({ updateUrl: false });
  };

  /** サイドバーでブロックが選択されたら、そのカードが見えるページへ移動する */
  #handleBlockSelect = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const card = target.closest('[data-clinic-card]');
    if (!(card instanceof HTMLElement)) return;

    if (!this.#filtered.some((item) => item.el === card)) {
      // 絞り込みで除外されている場合は条件を解除して再計算する
      this.#state.pref = '';
      this.#state.query = '';
      if (this.refs.search) this.refs.search.value = '';
      this.#render({ updateUrl: false });
    }

    const index = this.#filtered.findIndex((item) => item.el === card);
    if (index !== -1) {
      this.#state.page = Math.floor(index / this.#perPage) + 1;
      this.#render({ updateUrl: false });
    }

    card.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  /* -------------------------------------------------------------------- URL */

  #restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const prefParam = params.get('pref') ?? '';
    const prefName = prefParam ? PREF_NAME.get(prefParam) ?? prefParam : '';
    // 存在しない都道府県が指定された場合は「すべて」に落とす
    this.#state.pref = this.#cards.some((card) => card.pref === prefName) ? prefName : '';

    this.#state.query = params.get('q') ?? '';
    this.#state.page = Math.max(1, Number.parseInt(params.get('page') ?? '', 10) || 1);

    if (this.refs.search) this.refs.search.value = this.#state.query;
  }

  /**
   * 現在の状態に対応するURLを作る。既存の他パラメータは保持する。
   *
   * @param {{ pref: string, query: string, page: number }} state
   */
  #urlFor(state) {
    const params = new URLSearchParams(window.location.search);
    const slug = state.pref ? PREF_SLUG.get(state.pref) ?? state.pref : '';
    const query = state.query.trim();

    if (slug) params.set('pref', slug);
    else params.delete('pref');

    if (query) params.set('q', query);
    else params.delete('q');

    if (state.page > 1) params.set('page', String(state.page));
    else params.delete('page');

    const search = params.toString();
    return `${window.location.pathname}${search ? `?${search}` : ''}`;
  }

  /* ----------------------------------------------------------------- 描画 */

  /**
   * 絞り込み → ページ切り出し → 表示、の順で反映する。
   *
   * @param {{ updateUrl?: boolean }} [options]
   */
  #render({ updateUrl = true } = {}) {
    const query = this.#state.query.trim().toLowerCase();
    const { pref } = this.#state;

    this.#filtered = this.#cards.filter(
      (card) => (!pref || card.pref === pref) && (!query || card.search.includes(query))
    );

    const perPage = this.#perPage;
    const pageCount = Math.max(1, Math.ceil(this.#filtered.length / perPage));
    this.#state.page = Math.min(Math.max(1, this.#state.page), pageCount);

    const start = (this.#state.page - 1) * perPage;
    const pageItems = this.#filtered.slice(start, start + perPage);
    const visible = new Set(pageItems.map((card) => card.el));

    for (const { el } of this.#cards) {
      el.classList.toggle('clinic-card--hidden', !visible.has(el));
    }

    this.#renderCount(pageItems.length ? start + 1 : 0, start + pageItems.length);
    this.#renderChipState();
    this.#renderPager(pageCount);

    if (this.refs.noResults) this.refs.noResults.hidden = this.#filtered.length > 0;

    if (updateUrl) {
      const url = this.#urlFor(this.#state);
      if (url !== `${window.location.pathname}${window.location.search}`) {
        window.history.pushState(null, '', url);
      }
    }
  }

  /**
   * @param {number} from 1始まり。0件のときは0
   * @param {number} to
   */
  #renderCount(from, to) {
    const total = this.#filtered.length;

    this.refs.count.textContent =
      total === 0
        ? '該当 0件'
        : total <= to - from + 1
          ? `該当 ${total}件を表示`
          : `該当 ${total}件 / ${from}〜${to}件を表示`;
  }

  #renderChipState() {
    const { chips } = this.refs;
    if (!chips) return;

    for (const chip of chips.querySelectorAll('[data-pref-value]')) {
      const isActive = (/** @type {HTMLElement} */ (chip).dataset.prefValue ?? '') === this.#state.pref;
      chip.setAttribute('aria-pressed', String(isActive));
    }
  }

  /** @param {number} pageCount */
  #renderPager(pageCount) {
    const { pager } = this.refs;
    if (!pager) return;

    if (pageCount <= 1) {
      pager.hidden = true;
      pager.replaceChildren();
      return;
    }

    pager.hidden = false;

    const current = this.#state.page;
    const list = document.createElement('ul');
    list.className = 'clinic-list__pager-list';
    list.setAttribute('role', 'list');

    list.append(this.#pagerItem(current - 1, '前へ', { disabled: current === 1, rel: 'prev' }));

    let previous = 0;
    for (const page of pageWindow(current, pageCount)) {
      if (previous && page - previous > 1) {
        const gap = document.createElement('li');
        gap.className = 'clinic-list__pager-item';
        gap.innerHTML = '<span class="clinic-list__pager-gap" aria-hidden="true">…</span>';
        list.append(gap);
      }
      list.append(this.#pagerItem(page, String(page), { current: page === current }));
      previous = page;
    }

    list.append(this.#pagerItem(current + 1, '次へ', { disabled: current === pageCount, rel: 'next' }));

    pager.replaceChildren(list);
  }

  /**
   * @param {number} page
   * @param {string} label
   * @param {{ current?: boolean, disabled?: boolean, rel?: string }} [options]
   */
  #pagerItem(page, label, { current = false, disabled = false, rel } = {}) {
    const item = document.createElement('li');
    item.className = 'clinic-list__pager-item';

    if (disabled) {
      const span = document.createElement('span');
      span.className = 'clinic-list__pager-link clinic-list__pager-link--disabled';
      span.textContent = label;
      item.append(span);
      return item;
    }

    const link = document.createElement('a');
    link.className = 'clinic-list__pager-link';
    link.textContent = label;
    link.dataset.page = String(page);
    link.href = this.#urlFor({ ...this.#state, page });
    if (rel) link.rel = rel;
    if (current) {
      link.setAttribute('aria-current', 'page');
      link.classList.add('clinic-list__pager-link--current');
    }
    link.setAttribute('aria-label', `${page}ページ目`);

    item.append(link);
    return item;
  }
}

/**
 * 表示するページ番号（先頭・末尾・現在の前後2ページ）。
 *
 * @param {number} current
 * @param {number} pageCount
 * @returns {number[]}
 */
function pageWindow(current, pageCount) {
  const pages = new Set([1, pageCount]);

  for (let page = current - 2; page <= current + 2; page++) {
    if (page >= 1 && page <= pageCount) pages.add(page);
  }

  return [...pages].sort((a, b) => a - b);
}

if (!customElements.get('clinic-list-component')) {
  customElements.define('clinic-list-component', ClinicList);
}
